import styled from 'styled-components';
import { RoundButton } from '../chatSurfaces.styled';

/**
 * The picture opens as itself, not inside a modal: the modal's creme frame,
 * padding and header chrome all belong to a dialog, and here they only shrank
 * the photo and boxed it in.
 */

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  /* The app's modals sit at 130; matching it keeps the layering predictable. */
  z-index: 130;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
`;

/** Shrink-wraps the image, so a click beside it lands on the backdrop. */
export const Figure = styled.div`
  line-height: 0;
  max-width: 100%;
`;

export const BigImage = styled.img<{ $withStrip?: boolean }>`
  display: block;
  width: auto;
  height: auto;
  max-width: 90vw;
  /* With a strip below, the picture gives up the room it needs - and the pixel
     term is what protects a short window, where 74% of very little still is
     not enough to clear a 64px row of thumbnails. */
  max-height: ${({ $withStrip }) =>
    $withStrip ? 'min(74vh, calc(100vh - 180px))' : '90vh'};
  border-radius: 16px;
`;

/**
 * The screen's corner, not the picture's: every picture is a different size,
 * so a close pinned to the image moves with each one. Fixed here puts it in
 * the same place in the lightbox and in the image editor, which is the one
 * control both screens share.
 */
export const Close = styled(RoundButton)`
  position: fixed;
  top: 16px;
  right: 16px;
`;

/**
 * Against the screen edges rather than the picture, so they never cover it -
 * a portrait shot leaves plenty of room either side, and a landscape one would
 * otherwise have the arrows sitting on top of the subject.
 */
export const NavButton = styled(RoundButton)<{ $side: 'left' | 'right' }>`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => ($side === 'left' ? 'left: 24px;' : 'right: 24px;')}
  width: 40px;
  height: 40px;

  svg {
    width: 15px;
    height: 15px;
    /* One glyph, pointed both ways. */
    transform: ${({ $side }) => ($side === 'left' ? 'none' : 'rotate(180deg)')};
  }
`;

/**
 * Fixed to the bottom of the screen, not stacked under the picture: every
 * picture is a different height, so a strip that followed the one on screen
 * would jump up and down as you moved through them - and it is a control, not
 * part of the image.
 */
export const Strip = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 90vw;
  overflow-x: auto;
  padding: 4px;

  ::-webkit-scrollbar {
    height: 0;
  }
`;

export const Thumb = styled.button<{ $active: boolean }>`
  flex: none;
  width: 56px;
  height: 56px;
  padding: 0;
  border: none;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: none;
  /* Everything but the one on screen steps back. */
  opacity: ${({ $active }) => ($active ? 1 : 0.45)};
  outline: ${({ $active, theme }) =>
    $active ? `2px solid ${theme.white}` : 'none'};
  outline-offset: 2px;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  :hover {
    opacity: ${({ $active }) => ($active ? 1 : 0.75)};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;
